/* eslint-disable @typescript-eslint/no-explicit-any */
import { EmptyState } from "@/components/molecules/empty-state";
import { ErrorState } from "@/components/molecules/error-state";
import { PageSkeleton } from "@/components/molecules/page-skeleton";
import { DataTable } from "@/components/molecules/tables/data-table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DossierFiles } from "@/components/molecules/dossier-files";
import { useCustomer } from "@/features/customers/hooks/use-customers";
import {
  customerDisplayName,
  isNameUnsure,
} from "@/features/customers/name-verification";
import { UnsureNameMark } from "@/components/molecules/unsure-name-mark";
import type { Customer } from "@/features/customers/types";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";
import { PageTabs, PageTabsContent } from "@/components/molecules/page-tabs";
import { cn } from "@/lib/utils";
import {
  AddressBookIcon,
  BuildingsIcon,
  CakeIcon,
  EnvelopeIcon,
  GenderIntersexIcon,
  MapPinIcon,
  PencilIcon,
  PhoneIcon,
  type Icon,
} from "@phosphor-icons/react";
import type { ColumnDef } from "@tanstack/react-table";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { CallsTable } from "../calls/components/calls-table";
import { PageLayout } from "../layout";
import { CustomerEventsTable } from "./components/customer-events-table";
import { CustomerMessagesTable } from "./components/customer-messages-table";
import { CustomerQualificationsTable } from "./components/customer-qualifications-table";
import { CustomerRemindersTable } from "./components/customer-reminders-table";
import { useRemindersEnabled } from "@/features/reminders/hooks/use-reminders";
import { UnverifiedInfoIndicator } from "./components/unverified-info-indicator";
import { useAvailableTools } from "@/features/tools/hooks/use-tools";
import { usePermissions } from "@/features/clients/hooks/use-permissions";
import { isDemoMode } from "@/features/utils/demo-mode";

export function ContactsViewPage() {
  const { t } = useTranslation("customers");
  const { format } = useDateFormat();
  const { company: clientId, contactId } = useParams<{
    company: string;
    contactId: string;
  }>();
  const navigate = useNavigate();
  // The open tab is in the URL, so a link can point at a contact's calls and
  // the back button returns to the tab that was open.
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get("tab");
  const {
    data: customerAndCallback,
    isLoading,
    isError,
    refetch,
  } = useCustomer(clientId || "", contactId || "");
  const data = customerAndCallback?.customer;
  const callbackPreferences = customerAndCallback?.callback_preferences;
  const permissions = usePermissions();
  const { data: availableTools = [] } = useAvailableTools();

  // Helper to check permissions
  const hasPermission = (permission: string) =>
    permissions.includes(permission as any);

  // Helper to check if a tool is enabled
  const isToolEnabled = (toolName: string) => {
    const tool = availableTools.find((t) => t.tool.name === toolName);
    return tool?.clientTool?.enabled || false;
  };

  // Check if calendar/booking tool is enabled
  const calendarEnabled = isToolEnabled("booking");

  // Check if qualifications tool is enabled
  const qualificationsEnabled = isToolEnabled("qualifications");

  // Check if the Messagerie module is enabled — the sidebar's exact test, demo
  // mode included (its inbox is served from fixtures there).
  const messagesEnabled = isDemoMode() || isToolEnabled("messages");

  // Check if the scheduled reminders module is enabled
  const remindersEnabled = useRemindersEnabled();

  const backTo = {
    label: t("actions.backToCustomers"),
    path: `/${clientId}/contacts`,
  };

  if (isLoading)
    return <PageSkeleton width="medium" blocks={["h-56", "h-72"]} />;

  if (isError || !data) {
    return (
      <PageLayout title={t("view.title")} backTo={backTo} width="medium">
        {isError ? (
          <ErrorState onRetry={refetch} />
        ) : (
          <EmptyState icon={AddressBookIcon} title={t("view.notFound")} />
        )}
      </PageLayout>
    );
  }

  const customer = data;
  const fullName =
    customerDisplayName(customer, { prefix: true }) ||
    t("table.unnamedCustomer");
  const nameUnsure = isNameUnsure(customer);

  const getGenderLabel = (gender: string): string => {
    const key = `gender.${gender.toLowerCase()}` as const;
    const translated = t(key);
    return translated === key ? gender : translated;
  };

  // Relations columns
  const relationsColumns: ColumnDef<Partial<Customer>>[] = [
    {
      accessorKey: "display_name",
      header: t("view.name"),
      cell: ({ row }) => {
        const contact = row.original;
        const name = customerDisplayName(contact) || t("table.unnamedCustomer");
        return <span className="font-medium">{name}</span>;
      },
    },
    {
      accessorKey: "addresses.email",
      header: t("form.emails"),
      cell: ({ row }) => {
        const email = row.original.addresses?.email?.[0];
        return email?.address || "-";
      },
    },
    {
      accessorKey: "addresses.phone",
      header: t("form.phones"),
      cell: ({ row }) => {
        const phone = row.original.addresses?.phone?.[0];
        return phone?.number || "-";
      },
    },
    {
      accessorKey: "organization.company",
      header: t("form.company"),
      cell: ({ row }) => row.original.organization?.company || "-",
    },
  ];

  const hasOrganization = !!(
    customer.organization?.company ||
    customer.organization?.department ||
    customer.organization?.title
  );
  const hasCallbackPreferences = !!(
    callbackPreferences &&
    (callbackPreferences.next ||
      callbackPreferences.rrule ||
      callbackPreferences.text)
  );
  const emails = customer.addresses?.email || [];
  const phones = customer.addresses?.phone || [];
  const postals = customer.addresses?.postal || [];
  const relations = customer.relations?.contacts || [];

  // The tabs a contact's file has depend on what the tenant uses: no
  // appointments tab without the booking module, and so on.
  const tabs = [
    { value: "summary", label: t("view.tabs.summary") },
    { value: "calls", label: t("view.tabs.calls") },
    ...(hasPermission("calendar:view") && calendarEnabled
      ? [{ value: "events", label: t("view.tabs.events") }]
      : []),
    ...(remindersEnabled
      ? [{ value: "reminders", label: t("view.tabs.reminders") }]
      : []),
    ...(hasPermission("qualifications:view") && qualificationsEnabled
      ? [{ value: "qualifications", label: t("view.tabs.qualifications") }]
      : []),
    ...(hasPermission("messages:view") && messagesEnabled
      ? [{ value: "messages", label: t("view.tabs.messages") }]
      : []),
    ...(hasPermission("dossiers:view")
      ? [{ value: "files", label: t("view.tabs.files") }]
      : []),
  ];
  const tab = tabs.some((item) => item.value === tabParam)
    ? (tabParam as string)
    : "summary";

  return (
    <PageLayout
      title={fullName}
      titleSuffix={nameUnsure && <UnsureNameMark />}
      description={t("view.description")}
      backTo={backTo}
      width="medium"
      actions={
        <Button
          onClick={() => navigate(`/${clientId}/contacts/${contactId}/edit`)}
        >
          <PencilIcon className="h-4 w-4" weight="fill" />
          {t("actions.edit")}
        </Button>
      }
    >
      <PageTabs
        value={tab}
        onValueChange={(next) =>
          setSearchParams(next === "summary" ? {} : { tab: next }, {
            replace: true,
          })
        }
        tabs={tabs}
      >
        <PageTabsContent value="summary">
          <div className="grid items-start gap-6 lg:grid-cols-3">
            <div className="min-w-0 space-y-6 lg:col-span-2">
              {/* How to reach them, in one card: e-mails and phones side by
                  side, the postal addresses below. */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
                  <CardTitle>{t("view.contactDetails")}</CardTitle>
                  {customer.call_handling && (
                    <Badge
                      variant={
                        customer.call_handling === "blocked"
                          ? "destructive"
                          : "default"
                      }
                      title={t(
                        `form.callHandling.descriptions.${customer.call_handling}`,
                      )}
                    >
                      {t(`form.callHandling.options.${customer.call_handling}`)}
                    </Badge>
                  )}
                </CardHeader>
                <CardContent className="space-y-6">
                  {emails.length === 0 &&
                  phones.length === 0 &&
                  postals.length === 0 ? (
                    <EmptyState
                      compact
                      icon={AddressBookIcon}
                      title={t("view.noContactDetails")}
                    />
                  ) : (
                    <div className="grid gap-6 sm:grid-cols-2">
                      {emails.length > 0 && (
                        <DetailGroup
                          icon={EnvelopeIcon}
                          title={t("view.emailAddresses")}
                        >
                          {emails.map((email, idx) => (
                            <ContactLine
                              key={idx}
                              href={`mailto:${email.address}`}
                              value={email.address}
                              unverified={
                                customer.unverified_infos?.["addresses.email"]
                              }
                              type={
                                email.type &&
                                t(
                                  `contactTypes.email.${email.type}`,
                                  email.type,
                                )
                              }
                              primary={email.primary}
                            />
                          ))}
                        </DetailGroup>
                      )}
                      {phones.length > 0 && (
                        <DetailGroup
                          icon={PhoneIcon}
                          title={t("view.phoneNumbers")}
                        >
                          {phones.map((phone, idx) => (
                            <ContactLine
                              key={idx}
                              href={`tel:${phone.number}`}
                              value={phone.number}
                              unverified={
                                customer.unverified_infos?.["addresses.phone"]
                              }
                              type={
                                phone.type &&
                                t(
                                  `contactTypes.phone.${phone.type}`,
                                  phone.type,
                                )
                              }
                              primary={phone.primary}
                            />
                          ))}
                        </DetailGroup>
                      )}
                      {postals.length > 0 && (
                        <DetailGroup
                          icon={MapPinIcon}
                          title={t("view.postalAddresses")}
                          className="sm:col-span-2"
                        >
                          {postals.map((address, idx) => (
                            <div key={idx} className="space-y-1">
                              <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                                <span className="flex items-center text-sm">
                                  {address.full}
                                  <UnverifiedInfoIndicator
                                    unverifiedInfos={
                                      customer.unverified_infos?.[
                                        "addresses.postal"
                                      ]
                                    }
                                  />
                                </span>
                                <ContactBadges
                                  type={
                                    address.type &&
                                    t(
                                      `contactTypes.address.${address.type}`,
                                      address.type,
                                    )
                                  }
                                  primary={address.primary}
                                />
                              </div>
                              {(address.city ||
                                address.region ||
                                address.country) && (
                                <div className="text-sm text-muted-foreground">
                                  {[
                                    address.street,
                                    [address.postal_code, address.city]
                                      .filter(Boolean)
                                      .join(" "),
                                    address.region,
                                    address.country,
                                  ]
                                    .filter(Boolean)
                                    .join(" · ")}
                                </div>
                              )}
                            </div>
                          ))}
                        </DetailGroup>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>

              {customer.notes && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      {t("view.notes")}
                      <UnverifiedInfoIndicator
                        unverifiedInfos={customer.unverified_infos?.["notes"]}
                      />
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="whitespace-pre-wrap text-sm">
                      {customer.notes}
                    </div>
                  </CardContent>
                </Card>
              )}

              {relations.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t("view.relations")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <DataTable
                      variant="embedded"
                      columns={relationsColumns}
                      data={relations}
                      isLoading={false}
                      total={relations.length}
                      onRowNavigate={(contact) =>
                        contact.id
                          ? `/${clientId}/contacts/${contact.id}`
                          : null
                      }
                    />
                  </CardContent>
                </Card>
              )}
            </div>

            <div className="min-w-0 space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{t("view.personalInfo")}</CardTitle>
                </CardHeader>
                <CardContent>
                  <dl className="space-y-4">
                    <Detail label={t("view.name")}>
                      <span className="flex items-center font-medium">
                        {fullName}
                        {nameUnsure && <UnsureNameMark />}
                        <UnverifiedInfoIndicator
                          unverifiedInfos={[
                            customer.unverified_infos?.["given_name"],
                            customer.unverified_infos?.["family_name"],
                          ]
                            .flat()
                            .filter(Boolean)}
                        />
                      </span>
                    </Detail>
                    {customer.gender && (
                      <Detail
                        icon={GenderIntersexIcon}
                        label={t("view.gender")}
                      >
                        <span className="flex items-center">
                          {getGenderLabel(customer.gender)}
                          <UnverifiedInfoIndicator
                            unverifiedInfos={
                              customer.unverified_infos?.["gender"]
                            }
                          />
                        </span>
                      </Detail>
                    )}
                    {customer.birthday && (
                      <Detail icon={CakeIcon} label={t("view.birthday")}>
                        {format(customer.birthday, "PPP")}
                      </Detail>
                    )}
                    {hasOrganization && customer.organization.company && (
                      <Detail icon={BuildingsIcon} label={t("view.company")}>
                        <span className="flex items-center">
                          {customer.organization.company}
                          <UnverifiedInfoIndicator
                            unverifiedInfos={
                              customer.unverified_infos?.[
                                "organization.company"
                              ]
                            }
                          />
                        </span>
                      </Detail>
                    )}
                    {hasOrganization && customer.organization.department && (
                      <Detail label={t("view.department")}>
                        {customer.organization.department}
                      </Detail>
                    )}
                    {hasOrganization && customer.organization.title && (
                      <Detail label={t("view.jobTitle")}>
                        <span className="flex items-center">
                          {customer.organization.title}
                          <UnverifiedInfoIndicator
                            unverifiedInfos={
                              customer.unverified_infos?.["organization.title"]
                            }
                          />
                        </span>
                      </Detail>
                    )}
                    <Detail label={t("table.added")}>
                      {format(customer.created_at, "PPP")}
                    </Detail>
                  </dl>
                </CardContent>
              </Card>

              {hasCallbackPreferences && callbackPreferences && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t("view.callbackPreferences")}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <dl className="space-y-4">
                      {callbackPreferences.next && (
                        <Detail label={t("view.nextCallback")}>
                          {format(callbackPreferences.next, "PPPp")}
                        </Detail>
                      )}
                      {callbackPreferences.rrule && (
                        <Detail label={t("view.recurrenceRule")}>
                          <span className="font-mono text-xs">
                            {callbackPreferences.rrule}
                          </span>
                        </Detail>
                      )}
                      {callbackPreferences.text && (
                        <Detail label={t("view.callbackNote")}>
                          <span className="whitespace-pre-wrap">
                            {callbackPreferences.text}
                          </span>
                        </Detail>
                      )}
                    </dl>
                  </CardContent>
                </Card>
              )}
            </div>
          </div>
        </PageTabsContent>

        <PageTabsContent value="calls">
          <CallsTable customerId={contactId} limit={50} />
        </PageTabsContent>

        {tabs.some((item) => item.value === "events") && (
          <PageTabsContent value="events">
            <CustomerEventsTable customer={customer} limit={10} />
          </PageTabsContent>
        )}

        {remindersEnabled && (
          <PageTabsContent value="reminders">
            <CustomerRemindersTable customer={customer} limit={10} />
          </PageTabsContent>
        )}

        {tabs.some((item) => item.value === "qualifications") && (
          <PageTabsContent value="qualifications">
            <CustomerQualificationsTable customer={customer} limit={10} />
          </PageTabsContent>
        )}

        {tabs.some((item) => item.value === "messages") && (
          <PageTabsContent value="messages">
            <CustomerMessagesTable customer={customer} />
          </PageTabsContent>
        )}

        {tabs.some((item) => item.value === "files") && (
          <PageTabsContent value="files">
            <Card>
              <CardContent className="pt-6">
                <DossierFiles
                  customerId={customer.id}
                  source={{ origin: "contact" }}
                />
              </CardContent>
            </Card>
          </PageTabsContent>
        )}
      </PageTabs>
    </PageLayout>
  );
}

/** One labelled value of the side cards: the label small and muted above. */
function Detail({
  label,
  icon: IconComponent,
  children,
}: {
  label: React.ReactNode;
  icon?: Icon;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      <dt className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        {IconComponent && <IconComponent className="size-3.5" aria-hidden />}
        {label}
      </dt>
      <dd className="text-sm">{children}</dd>
    </div>
  );
}

function DetailGroup({
  icon: IconComponent,
  title,
  className,
  children,
}: {
  icon: Icon;
  title: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <section className={cn("min-w-0 space-y-3", className)}>
      <h3 className="flex items-center gap-1.5 text-xs font-medium text-muted-foreground">
        <IconComponent className="size-3.5" aria-hidden />
        {title}
      </h3>
      {children}
    </section>
  );
}

function ContactLine({
  href,
  value,
  unverified,
  type,
  primary,
}: {
  href: string;
  value: string;
  unverified?: React.ComponentProps<
    typeof UnverifiedInfoIndicator
  >["unverifiedInfos"];
  type?: string | false;
  primary?: boolean;
}) {
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
      <span className="flex min-w-0 items-center">
        <a
          href={href}
          className="truncate text-sm text-primary-strong hover:underline"
        >
          {value}
        </a>
        <UnverifiedInfoIndicator unverifiedInfos={unverified} />
      </span>
      <ContactBadges type={type} primary={primary} />
    </div>
  );
}

function ContactBadges({
  type,
  primary,
}: {
  type?: string | false;
  primary?: boolean;
}) {
  const { t } = useTranslation("customers");
  if (!type && !primary) return null;
  return (
    <span className="flex gap-1.5">
      {type && (
        <Badge variant="outline" className="text-xs">
          {type}
        </Badge>
      )}
      {primary && (
        <Badge variant="default" className="text-xs">
          {t("common.primary")}
        </Badge>
      )}
    </span>
  );
}
